import { useEffect, useState } from 'react';
import type { ChatBalanceEntry, HistoryRange } from '@wave/shared';
import { TrendChart } from './TrendChart';
import { CcyChart } from './CcyChart';
import { MoversChart } from './MoversChart';
import { ConcentrationChart } from './ConcentrationChart';
import { DailyChart } from './DailyChart';
import { DayReconPanel } from './DayReconPanel';

/**
 * Canvas-рендерер ECharts не понимает строки `var(--x)` (CSS-переменные
 * резолвятся только в DOM/SVG-контекстах) — резолвим токены темы в hex
 * на старте модуля. Fallback'и = фактические значения светлой темы.
 */
function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export const CHART_COLORS = {
  net: cssVar('--accent', '#2563eb'),
  pos: cssVar('--red', '#dc2626'),
  neg: cssVar('--green', '#16a34a'),
  grid: cssVar('--border', '#e2e8f0'),
  text: cssVar('--text2', '#64748b'),
};
export const BASE_GRID = { left: 56, right: 16, top: 32, bottom: 48 };

/** Поля сетки: на телефоне уже, чтобы ширина доставалась самому графику. */
export function chartGrid(narrow: boolean) {
  return narrow ? { left: 40, right: 8, top: 36, bottom: 48 } : BASE_GRID;
}

/** «135k» / «−1.2M» — компактные подписи оси на телефоне (точные числа — в тултипе). */
export function compactNum(v: number): string {
  const a = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (a >= 1e6) return `${sign}${+(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}${Math.round(a / 1e3)}k`;
  return `${sign}${a}`;
}

/** Подписи оси значений. */
export function valueAxisLabel(narrow: boolean) {
  return narrow
    ? { color: CHART_COLORS.text, fontSize: 10, formatter: compactNum }
    : { color: CHART_COLORS.text };
}

/** Легенда: на телефоне — одна прокручиваемая строка, иначе она переносится поверх графика. */
export function chartLegend(narrow: boolean) {
  return narrow
    ? { type: 'scroll' as const, top: 0, left: 0, right: 0, textStyle: { color: CHART_COLORS.text, fontSize: 11 }, itemGap: 8 }
    : { top: 0, textStyle: { color: CHART_COLORS.text } };
}

/**
 * Тултип на телефоне: не выходит за график и переносит длинные строки.
 * `pointer-events: none` — тултип ложится под палец и иначе перехватывал бы
 * повторный тап, который открывает действие (см. EChart onGridClick).
 */
export function tooltipFit(narrow: boolean) {
  return narrow
    ? { confine: true, extraCssText: 'max-width: calc(100vw - 48px); white-space: normal; pointer-events: none;' }
    : {};
}

const RANGES: { key: HistoryRange; label: string }[] = [
  { key: '24h', label: '24ч' },
  { key: '7d', label: '7д' },
  { key: '30d', label: '30д' },
  { key: '90d', label: '90д' },
  { key: 'all', label: 'Всё' },
];

type Tab = 'trend' | 'daily' | 'recon' | 'ccy' | 'movers' | 'concentration';
const TABS: { key: Tab; label: string }[] = [
  { key: 'trend', label: 'Сводный тренд' },
  { key: 'daily', label: 'По дням' },
  { key: 'recon', label: 'Сверка дня' },
  { key: 'ccy', label: 'По валютам' },
  { key: 'movers', label: 'Топ изменений' },
  { key: 'concentration', label: 'Концентрация' },
];

const LS_KEY = 'balances.chartsOpen';
const LS_SMOOTH = 'balances.charts.smooth';

export function ChartsPanel({
  liveChats,
  onOpenChat,
}: {
  liveChats: ChatBalanceEntry[];
  onOpenChat: (chatId: string, title: string) => void;
}) {
  const [open, setOpen] = useState(() => localStorage.getItem(LS_KEY) === '1');
  const [tab, setTab] = useState<Tab>('trend');
  const [range, setRange] = useState<HistoryRange>('7d');
  // Сутки для «Сверки дня»: null — сегодня. Живёт здесь, чтобы клик по свече
  // во вкладке «По дням» открывал сверку именно этих суток.
  const [reconDate, setReconDate] = useState<string | null>(null);

  // Сглаживание — только визуальное (тултип всегда показывает сырые значения).
  const [smooth, setSmooth] = useState(() => localStorage.getItem(LS_SMOOTH) !== '0');
  useEffect(() => {
    localStorage.setItem(LS_SMOOTH, smooth ? '1' : '0');
  }, [smooth]);

  useEffect(() => {
    localStorage.setItem(LS_KEY, open ? '1' : '0');
  }, [open]);

  return (
    <div className="mb-5 rounded-lg border border-border bg-bg2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-text transition-colors duration-150 hover:bg-bg3"
      >
        <span>📈 Графики</span>
        <span className="text-text2">{open ? '▲ свернуть' : '▼ развернуть'}</span>
      </button>

      {open && (
        <div className="border-t border-border px-3 pb-3 sm:px-4 sm:pb-4">
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {/* 6 вкладок не влезают в телефон — горизонтальная прокрутка вместо переноса. */}
            <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-border bg-bg p-0.5">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`h-8 shrink-0 whitespace-nowrap rounded px-3 text-sm font-medium transition-colors duration-150 ${
                    tab === t.key ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {tab === 'trend' && (
              <label
                className="flex items-center gap-1.5 text-xs text-text2"
                title="Линии — скользящая медиана по времени (24ч: 2 ч, 7д: 6 ч, 30д: сутки): переводы «в пути» не дёргают уровень. Бледный коридор — сырой разброс позиции. Значения в тултипе — сырые."
              >
                <input type="checkbox" checked={smooth} onChange={(e) => setSmooth(e.target.checked)} />
                Устойчивые линии
              </label>
            )}
            {tab !== 'concentration' && tab !== 'recon' && (
              <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    className={`h-8 rounded px-2.5 text-xs font-medium transition-colors duration-150 ${
                      range === r.key ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3">
            {tab === 'trend' && <TrendChart range={range} smooth={smooth} />}
            {tab === 'daily' && (
              <DailyChart
                range={range}
                onPickDay={(date) => {
                  setReconDate(date);
                  setTab('recon');
                }}
              />
            )}
            {tab === 'recon' && <DayReconPanel date={reconDate} onDateChange={setReconDate} onOpenChat={onOpenChat} />}
            {tab === 'ccy' && <CcyChart range={range} />}
            {tab === 'movers' && <MoversChart range={range} onOpenChat={onOpenChat} />}
            {tab === 'concentration' && <ConcentrationChart liveChats={liveChats} />}
          </div>
        </div>
      )}
    </div>
  );
}

/** Пустое состояние вкладок, пока истории мало. */
export function EmptyHistory({ pointsCount }: { pointsCount: number }) {
  if (pointsCount >= 2) return null;
  return (
    <div className="flex h-40 items-center justify-center text-sm text-text2">
      История копится с момента включения. Первые графики появятся через ~30 минут.
    </div>
  );
}
